import Script from 'next/script';
import { FileDownloadService } from '../../services/file-download.service';
import { MetricsService } from '../../services/metrics.service';
import { MetricEventName } from '../../types/metric-events';
import { Component } from 'react';
import { debounceTime } from 'rxjs/operators';
import ContactUsModal from '../../components/ui/contact-us-modal/contact-us.modal';
import { FileListComponent } from '../../components/ui/file-list/file-list.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import { lazyInject } from '../../context/module';
import { FileUpload } from '../../models/file-upload';
import { BulkZipService } from '../../services/bulk-zip.service';
import { FileManagerService } from '../../services/file-manager.service';
import { LoginService } from '../../services/login.service';
import { MotivesService } from '../../services/motives.service';
import { SettingsService } from '../../services/settings.service';
import { WindowService } from '../../services/window.service';
import { PSDate } from '../../utils/date.utils';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import styles from './file-download.component.module.scss';

export default class FileDownloadComponent extends Component<any> {
    public fileArray: FileUpload[] = [];
    /// This prevent SEO CLS jump
    private fileArrayCalculated = false;

    public recoveredFileUploads: FileUpload[] = [];
    public activeFileUploads: FileUpload[] = [];
    motives: MotiveConfig;

    public showHelpUsMessage = true;
    private isFreeUser = true;
    public internalAdsEnabled = false;
    public hasFiles = false;
    private bulkZipEnabled = false;

    @lazyInject(FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(MetricsService) private metricsService: MetricsService;
    @lazyInject(WindowService) public windowService: WindowService;
    @lazyInject(MotivesService) public motivesService: MotivesService;
    @lazyInject(LoginService) public loginService: LoginService;
    @lazyInject(BulkZipService) private bulkZipService: BulkZipService;
    @lazyInject(SettingsService) private settingsService: SettingsService;
    @lazyInject(FileDownloadService) private fileDownloadService: FileDownloadService;

    constructor(props) {
        super(props);
    }

    componentDidMount() {
        this.loginService.onSessionDetails
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (details) => {
                    this.isFreeUser = !details.isPaidUser;
                },
                error: (error) => {
                    console.error(error);
                },
            });

        this.motivesService.onEffectiveMotiveSettingsChange
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (motives) => {
                    this.showHelpUsMessage = this.isFreeUser && motives.headerPromotionToBuy;
                    this.internalAdsEnabled = this.motives && this.motives.internalMotivesEnabled;
                },
                error: (error) => {
                    console.error(error);
                },
            });

        this.fileManagerService.fileUploadsListChanged
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (fileUploads) => {
                    this.updateFileArrayForDisplay(fileUploads);
                },
                error: (error) => {
                    console.error(error);
                },
            });

        this.fileManagerService.fileUploadChanged.pipe(
            untilDestroyed(this),
            debounceTime(100)
        ).subscribe({
            next: (fileUpload) => {
                this.forceUpdate();
            }
        })

        this.settingsService.featureFlags
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (ff) => {
                    const enabled = !!ff?.bulkZip;
                    if (enabled !== this.bulkZipEnabled) {
                        this.bulkZipEnabled = enabled;
                        this.forceUpdate();
                    }
                },
                error: (error) => console.error(error),
            });
    }

    componentWillUnmount() {
        // for untilDestroyed
    }


    updateFileArrayForDisplay(fileUploads: FileUpload[]) {
        this.fileArray = [];
        fileUploads.forEach(fileUpload => {
            this.fileArray.push(fileUpload);
        });
        this.fileArray = this.fileArray.sort((a, b) =>
            PSDate.from(a.processStartTime).isBefore(b.processStartTime) ? 1 : -1);
        this.activeFileUploads = this.fileArray.filter(f => !f.recoveredFileUpload);
        this.recoveredFileUploads = this.fileArray.filter(f => f.recoveredFileUpload);
        this.hasFiles = !!(this.recoveredFileUploads.length || this.activeFileUploads.length);
        this.fileArrayCalculated = true;
        this.forceUpdate();
    }

    showContactUs(e: MouseEvent) {
        e.preventDefault();
        ContactUsModal.show({});
    }

    private getAllOutputFileIds(): string[] {
        return this.fileArray.flatMap((fu) => {
            const outs = (fu.result?.outputFiles as string[] | undefined) || [];
            return outs.filter(Boolean);
        });
    }

    /** 'idle' | 'submitting' | 'error' — the button was silent in all three. */
    private zipState: 'idle' | 'submitting' | 'error' = 'idle';
    /** What actually went wrong, so the message can be true rather than generic. */
    private zipError: string | null = null;

    /**
     * Ask for a ZIP of every output, and say what is happening.
     *
     * This used to fire the request and return: no busy state, no confirmation, no error -- the
     * failure path was a console.error nobody sees. The job does start, and it lands in the
     * Downloads Center behind the header pill, which on a phone is inside the hamburger. So from
     * the user's side a working request was indistinguishable from a dead button, and that is
     * exactly how it was reported.
     *
     * On success the Downloads Center opens, so the thing they asked for is on screen instead of
     * somewhere they have no reason to look.
     */
    private onBulkDownload = async () => {
        const ids = this.getAllOutputFileIds();
        if (ids.length < 2 || this.zipState === 'submitting') return;
        this.zipState = 'submitting';
        this.zipError = null;
        this.forceUpdate();
        try {
            await this.bulkZipService.submit(ids);
            this.zipState = 'idle';
            this.forceUpdate();
            // Put the job in front of them rather than leaving it in the header.
            this.bulkZipService.openCenter();
        } catch (err) {
            console.error('[bulk-zip] submit failed', err);
            // Say which thing went wrong. "Try again" is actively misleading for the
            // rate-limit case, where trying again is exactly what will not help.
            const status = (err as any)?.status ?? (err as any)?.response?.status;
            this.zipError = status === 429
                ? 'You already have several ZIPs being prepared. Wait for one to finish, then try again.'
                : status === 404
                    ? 'Some of these files are no longer available to zip. Download them individually.'
                    : 'We couldn\'t start the ZIP. Download the files individually, or try again.';
            this.zipState = 'error';
            this.forceUpdate();
        }
    };

    getRotatingCTA() {
        const ctas = [
            <>⭐ If XConvert saved you time, a <a href="https://www.trustpilot.com/evaluate/xconvert.com" target="_blank" rel="noopener">quick Trustpilot review</a> helps us stay free.</>,
            <>📋 Know someone who needs this? <a href="/how-to-help-xconvert-to-grow">Share XConvert</a> and help others find us.</>,
            <>🙏 Enjoying XConvert? <a href="/how-to-help-xconvert-to-grow">Here are quick ways to help us grow</a>.</>,
        ];
        return ctas[Math.floor(Date.now() / 60000) % ctas.length];
    }

    getFileLists(props) {
        return <div data-jkey="file-lists">
            <div>
                <FileListComponent {...props} sectionTitle="Downloadable Files" fileUploadArray={this.fileArray}
                    parentClasses="" cardClasses="">
                    {
                        <div className="gcontent-container">
                            <MotiveComponent
                                data={AdSlots.inContent} containerWidth="100%" containerHeight="60px"
                                location={MotiveLocationTypes.fileDownloadPageWithInCurrentDownloads} adWidth="720px" adHeight="60px"
                                showAds={true} className='width_major_md'
                            ></MotiveComponent>
                        </div>
                    }

                </FileListComponent>
            </div>
        </div>;
    }

    render() {
        if (!this.fileArrayCalculated) {
            return <div style={{ minHeight: '1000px' }}></div>;
        }
        return <>
            <Script type="text/javascript" dangerouslySetInnerHTML={{
                __html: `(function(d, s, id) {
var js, fjs = d.getElementsByTagName(s)[0];
if (d.getElementById(id)) return;
js = d.createElement(s); js.id = id;
js.src = "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v3.0";
fjs.parentNode.insertBefore(js, fjs);
}(document, 'script', 'facebook-jssdk'));
`,
            }}
            >
            </Script>
            <div className={styles['wrapper']}>
                <div className={styles['main-card']}>
                    <div>
                        {
                            <div className="">
                                <MotiveComponent
                                    data={AdSlots.pageTop} containerWidth="100%" containerHeight="90px"
                                    location={MotiveLocationTypes.fileDownloadPageTop} adWidth="970px" adHeight="90px" showAds={true} className='width_major'
                                ></MotiveComponent>
                            </div>
                        }
                    </div>

                    {
                        this.showHelpUsMessage && <div className={styles['help-message']}>
                            {this.getRotatingCTA()}
                        </div>
                    }

                    <div className='row mt-3'>
                        <div className='col-md-2 d-none d-md-block'>
                            {
                                <div className="">
                                    <MotiveComponent
                                        data={AdSlots.desktopRail} containerWidth="100%" containerHeight="600px"
                                        location={MotiveLocationTypes.fileDownloadPageLeft} adWidth="160px" adHeight="600px" showAds={true} className='height_major'
                                    ></MotiveComponent>
                                </div>
                            }
                        </div>
                        <div className="col-12 col-md-8">
                            {this.bulkZipEnabled && this.getAllOutputFileIds().length >= 2 && (
                                <div className="mb-3">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        disabled={this.zipState === 'submitting'}
                                        aria-busy={this.zipState === 'submitting'}
                                        onClick={this.onBulkDownload}
                                    >
                                        {this.zipState === 'submitting'
                                            ? 'Preparing your ZIP…'
                                            : `📦 Download all (ZIP) · ${this.getAllOutputFileIds().length} files`}
                                    </button>
                                    {this.zipState === 'error' && this.zipError && (
                                        <p role="status" className="text-danger mb-0 mt-2" style={{ fontSize: '0.85rem' }}>
                                            {this.zipError}
                                        </p>
                                    )}
                                </div>
                            )}
                            {
                                this.getFileLists(this.props)
                            }
                            <div>
                                <div>
                                    {
                                        !this.hasFiles && <p className="text-muted mt-3">
                                            No files found. Your processed and in-progress files will appear here.
                                        </p>
                                    }

                                    <div className="mt-5">
                                        <h5>About My Downloads</h5>
                                        <p className="text-secondary">
                                            This page displays your in-progress and processed files. Use the controls on each card to download, re-edit, or check the status of your files.
                                        </p>
                                    </div>
                                </div>

                                <div className={styles['faq-section']}>
                                    <h5>Frequently Asked Questions</h5>

                                    <div className={styles['faq-item']}>
                                        <strong>Why is my download slow?</strong>
                                        <p>Network speed usually depends on the distance between you and our servers. We do our best to route you to the nearest server, but internet traffic can affect speeds.</p>
                                    </div>

                                    <div className={styles['faq-item']}>
                                        <strong>Why is the compressed file sometimes larger?</strong>
                                        <p>Compression results depend on the file content and settings. Occasionally, certain settings may increase file size. Try using the <b>Re-edit</b> button to adjust compression levels.</p>
                                    </div>

                                    <div className={styles['faq-item']}>
                                        <strong>Are my files private?</strong>
                                        <p>Yes. We only store files for a few hours before they are permanently deleted. You can also manually delete files instantly using the trash icon.</p>
                                    </div>

                                    <div className={styles['faq-item']}>
                                        <strong>Can I close the browser while processing?</strong>
                                        <p>Yes. Once processing starts, you can close the tab. If you return to this page within a few hours, your files will be here ready for download.</p>
                                    </div>
                                </div>

                                <div className={styles['convert-another']}>
                                    {/* Was eight fixed pills that ignored what the user had just made.
                                        Now the first row is what people who produced THIS output type
                                        actually did next, measured over 14 days of production switches
                                        (docs/research/next-tool-transitions.md). The generic tools stay
                                        underneath, since not every visit is a continuation. */}
                                    {/* Per-file continuations moved onto the result cards, where
                                        they can name the file they act on. This block is back to
                                        being what it always was: somewhere to start something
                                        new. */}
                                    <h5>Convert Another File</h5>
                                    <p className="text-secondary" style={{fontSize: '0.9rem'}}>
                                        Need to process more files? Jump back to one of our popular tools.
                                    </p>
                                    <div className={styles['tools-grid']}>
                                        {[
                                            ['Image Compressor', 'image-compressor'], ['Video Compressor', 'video-compressor'],
                                            ['Audio Compressor', 'audio-compressor'], ['Compress PDF', 'compress-pdf'],
                                            ['Image Converter', 'image-converter'], ['Video Converter', 'video-converter'],
                                            ['Audio Converter', 'audio-converter'], ['Merge to PDF', 'merge-image-to-pdf'],
                                        ].map(([name, slug], i) => <a key={i} href={`/${slug}`} className={styles['tool-pill']}>{name}</a>)}
                                    </div>
                                </div>                            </div>
                        </div>

                        <div className='col-md-2 d-none d-md-block'>
                            {
                                <div className="">
                                    <MotiveComponent
                                        data={AdSlots.desktopRail} containerWidth="100%" containerHeight="600px"
                                        location={MotiveLocationTypes.fileDownloadPageRight} adWidth="160px" adHeight="600px" showAds={true} className='height_major'
                                    ></MotiveComponent>
                                </div>
                            }
                        </div>
                    </div>

                    <div>
                        <div>
                            <div>
                                {
                                    <div className="">
                                        <MotiveComponent
                                            data={AdSlots.aboveFooter} containerWidth="100%" containerHeight="90px"
                                            location={MotiveLocationTypes.fileDownloadPageBottom} adWidth="970px" adHeight="90px" showAds={true}
                                            className='width_major'
                                        ></MotiveComponent>
                                    </div>
                                }

                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    }
}
